import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type AssetKind = 'image' | 'video' | 'audio' | '3d' | 'document';
export type AssetState = 'active' | 'inactive' | 'deleted' | 'private' | 'quarantined';

/** Counts for the current filters; each dimension ignores its own filter. */
export type AssetFacets = {
  kind: Record<AssetKind | 'other', number>;
  state: Record<AssetState, number>;
};

/** Query params of GET /api/admin/assets (all optional). */
export type AssetListParams = {
  page_size?: number;
  q?: string;
  type?: string;
  state?: string;
  role?: string;
  eid?: string;
  uid?: string;
  oid?: string;
  has_phash?: string;
  date_from?: string;
  date_to?: string;
  /** `<signal_key>` or `<signal_key>:<label>`, e.g. `nudity:unsafe`. */
  signal?: string;
  moderation?: string;
  /** Comma-separated asset IDs (max 500). */
  asids?: string;
  sort?: string;
  facets?: 0 | 1;
};

export type AssetListResponse = {
  assets: Asset[];
  total: number;
  total_pages: number;
  page: number;
  page_size: number;
  sort: string;
  facets?: AssetFacets;
};

const useGetQueryFn = () => {
  const request = useClientStore((state) => state.request);

  return withErrorHandling(
    async (page: number = 1, additional: AssetListParams = {}): Promise<AssetListResponse> => {
      const params: Record<string, string> = {};
      Object.entries({ page, ...additional }).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params[key] = String(value);
      });
      const raw: any = await request(`/api/admin/assets?${new URLSearchParams(params).toString()}`);
      // The envelope may arrive flat or nested under `data`.
      const body = raw?.data && Array.isArray(raw.data.assets) ? raw.data : raw ?? {};
      return {
        ...body,
        assets: Array.isArray(body.assets) ? body.assets : [],
        total: body.total ?? 0,
        total_pages: body.total_pages ?? 0,
        page: body.page ?? page,
      };
    },
    'An unexpected error occurred while fetching assets',
  );
};

/** Key factory so mutations (delete/restore) can invalidate the whole list. */
export const assetKeys = {
  all: ['assets'] as const,
};

export const useAssetQuery = (page = 1, params: AssetListParams = {}) => {
  const queryFn = useGetQueryFn();

  return useQuery({
    queryKey: ['assets', page, params],
    queryFn: () => queryFn(page, params),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};
